import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { pageRoutes } from '../../routes/page-routes';
import { ApiError } from '../../shared/api/problem';
import { useSession } from '../../shared/api/session';
import { Alert } from '../../shared/ui/Alert';
import { Button } from '../../shared/ui/Button';
import { cardClass } from '../../shared/ui/Card';
import { cn } from '../../shared/ui/cn';
import { Field } from '../../shared/ui/Field';
import { signIn } from './auth.api';
import { useSessionRestored } from './session-restore';

/** Where to go after signing in: the page that sent us here, if it is one of ours. */
function returnPath(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'from' in state && typeof state.from === 'string') {
    return state.from.startsWith('/') && !state.from.startsWith('//') ? state.from : pageRoutes.dashboard;
  }
  return pageRoutes.dashboard;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'rate_limited') return 'Too many attempts. Wait a minute and try again.';
    return error.problem.detail;
  }
  return 'Sign-in failed. Please try again.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const restored = useSessionRestored();
  const { accessToken } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const from = returnPath(location.state);

  const login = useMutation({
    mutationFn: () => signIn(email, password),
    onSuccess: () => navigate(from, { replace: true }),
  });

  if (restored && accessToken) return <Navigate to={from} replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <main className="grid min-h-svh place-items-center bg-linear-160 from-navy via-navy-light via-60% to-brand p-4">
      <form
        className={cn(cardClass, 'grid w-full max-w-100 gap-4')}
        onSubmit={submit}
        aria-labelledby="login-title"
      >
        <h1 id="login-title" className="text-xl font-bold">
          Sawa Kart · Import system
        </h1>
        <p className="text-muted">Sign in to follow and start product imports.</p>
        {login.isError ? <Alert>{messageFor(login.error)}</Alert> : null}
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" busy={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </main>
  );
}
